import {
  DailyEventObjectParticipant,
  DailyEventObjectParticipantLeft,
  DailyEventObjectParticipants,
} from '@daily-co/daily-js';
import { useDaily, useThrottledDailyEvent } from '@daily-co/daily-react';
import deepEqual from 'fast-deep-equal';
import { useMemo } from 'react';
import { atom, useRecoilCallback, useRecoilValue } from 'recoil';

import { getPrebuiltUserData } from '/lib/userData';

const userDataState = atom<Record<string, unknown>>({
  key: 'user-data-state',
  default: {},
});

type Filter<T> = (id: string, data: T) => boolean;

const noopFilter = () => true;

/**
 * Returns React-ified userData objects for each passed session_id.
 */
export const useUserData = <T = unknown>(
  ids: string[],
  filter: Filter<T> = noopFilter
): Record<string, T> => {
  const userData = useRecoilValue(userDataState);
  const daily = useDaily();

  useThrottledDailyEvent(
    [
      'joined-meeting',
      'participant-joined',
      'participant-updated',
      'participant-left',
    ],
    useRecoilCallback(
      ({ transact_UNSTABLE }) =>
        (
          evts: (
            | DailyEventObjectParticipants
            | DailyEventObjectParticipant
            | DailyEventObjectParticipantLeft
          )[]
        ) => {
          transact_UNSTABLE(({ get, set }) => {
            const prev = get(userDataState);
            const newState = { ...prev };
            evts.forEach((ev) => {
              switch (ev.action) {
                case 'joined-meeting': {
                  const session_id = ev.participants.local.session_id;
                  const userData = getPrebuiltUserData(daily, 'local');
                  newState[session_id] = userData;
                  break;
                }
                case 'participant-joined':
                case 'participant-updated': {
                  const session_id = ev.participant.session_id;
                  const userData = getPrebuiltUserData(
                    daily,
                    ev.participant.local ? 'local' : session_id
                  );
                  newState[session_id] = userData;
                  break;
                }
                case 'participant-left': {
                  const session_id = ev.participant.session_id;
                  delete newState[session_id];
                  break;
                }
              }
            });
            if (deepEqual(prev, newState)) return;
            set(userDataState, newState);
          });
        },
      [daily]
    )
  );

  return useMemo(
    () =>
      Object.entries(userData)
        .filter(([id]) => ids.includes(id))
        // Apply custom filter
        .filter(([id, data]) => filter(id, data as T))
        // Convert array back to object
        .reduce((res, [id, data]) => {
          res[id] = data;
          return res;
        }, {}),
    [filter, ids, userData]
  );
};
